import { type PayableSubType } from '@spendesk/bookkeeping-core-types';
import { type ReactNode } from 'react';

import { useAccountingSoftwareName } from 'modules/accounting-integration/apis';
import { useNotifications } from 'modules/app/notifications';
import { SupplierOrMemberSyncStatus } from 'modules/bookkeep/components/SupplierOrMemberSyncStatus';
import { useGetMemberSyncStatus } from 'modules/bookkeep/hooks';
import type { Payable } from 'modules/bookkeep/payables/containers/PayablePanelContainer/hooks/usePayableQuery/payable';
import { type RawPayableType } from 'modules/bookkeep/payables/containers/PayablePanelContainer/hooks/usePayableQuery/query';
import {
  getIsCardPayable,
  getIsCreditNotePayable,
  getIsInvoicePayable,
  isSpanishDPRPayable,
  type PayableType,
} from 'modules/bookkeep/payables/models/payable';
import { formatMemberName } from 'modules/bookkeep/prepare-payables/models';
import { InvoiceCardSection } from 'modules/cards/components/Payables/InvoiceCardSection';
import {
  useIsEditablePayable,
  useUpdatePayable,
  useUpdatePayableErrorMessage,
  useIsLockedPayable,
  useWasUnlockablePayable,
} from 'modules/payable/hooks';
import { ExpenditurePanelHeader } from 'src/core/common/components/ExpenditurePanelHeader';
import { useTranslation } from 'src/core/common/hooks/useTranslation';
import { AnalyticEventName, track } from 'src/core/utils/analytics';
import { payableTypeToExpenditureType } from 'src/core/utils/entities/expenditure';
import { formatMonetaryValue } from 'src/core/utils/monetaryValue';
import { logger } from 'src/utils/datadog-log-wrapper';

import { EditAfterExportEditCallout } from './EditAfterExportEditCallout';
import { PayablePanelLockFeedback } from './PayablePanelLockFeedback';
import { PayablePanelStatus } from './PayablePanelStatus';
import { ReversalsAllocatedSection } from './ReversalsAllocatedSection';
import {
  getCounterpartyMembersCalloutText,
  getCounterpartyMembersTitle,
  getCounterpartySuppliersTitle,
  getCounterpartySuppliersTooltipText,
} from './translations';
import { useGetSupplierSyncWithDefault } from '../../../prepare-payables/components/PreparePayablesTable/hooks/useGetSupplierSyncWithDefault';
import { CreditNotesCallout } from '../../containers/PayableDetailsSectionContainer/CreditNotesCallout';
import { useUpdatePayableQueryCache } from '../../containers/PayablePanelContainer/hooks';

export const PayablePanelHeader = ({
  payable,
  exportError,
  panelActions,
}: {
  payable: Payable;
  exportError?: ReactNode;
  panelActions?: ReactNode;
  // eslint-disable-next-line sonarjs/cognitive-complexity
}) => {
  const { t } = useTranslation('global');

  const amountInCompanyCurrency = formatMonetaryValue(payable.functionalAmount);

  const amountInExpenditureCurrency =
    payable.grossAmount.currency !== payable.functionalAmount.currency
      ? formatMonetaryValue(payable.grossAmount)
      : undefined;

  const isLockedPayable = useIsLockedPayable(payable);
  const wasUnlockablePayable = useWasUnlockablePayable(payable);
  const shouldDisplayDPRInformations = isSpanishDPRPayable(payable);
  const isEditablePayable = useIsEditablePayable(payable);
  const isInvoicePayable = getIsInvoicePayable(payable);
  const isCreditNotePayable = getIsCreditNotePayable(payable);
  const isCardPayable = getIsCardPayable(payable);

  const isSupplierDetailsEditable =
    isEditablePayable &&
    (shouldDisplayDPRInformations
      ? isInvoicePayable || isCreditNotePayable || isCardPayable
      : isCardPayable);

  const isSupplierEditable = isSupplierDetailsEditable && isCardPayable;

  const payableType = payableTypeToExpenditureType({
    type: reshapePayableType(payable.type),
    subtype: payable.subtype,
  });

  // Supplier details

  let supplierId =
    payable.counterparty?.type === 'supplier'
      ? payable.counterparty?.id
      : payable.supplier?.id;
  supplierId = supplierId !== 'unknown' ? supplierId : undefined;

  const supplierName =
    payable.counterparty?.type === 'supplier'
      ? payable.counterparty.name
      : payable.supplier?.name || '';

  const onSupplierChange = useOnSupplierChange({ payable, isSupplierEditable });

  // Expenditure props

  const expenditureProps = {
    date: null,
    amountInCompanyCurrency,
    amountInExpenditureCurrency,
    feesAmountInCompanyCurrency: undefined,
    user: {
      id: payable.member?.id ?? '',
      name: formatMemberName(payable.member, t),
      email:
        payable.member?.type !== 'suspendedUser'
          ? payable.member?.email
          : undefined,
      avatar:
        payable.member?.type !== 'invitee' ? payable.member?.avatar : undefined,
      syncStatus: payable.member?.id ? (
        <EmployeeSyncStatus
          userId={payable.member.id}
          userName={formatMemberName(payable.member, t)}
        />
      ) : undefined,
    },
  };

  return (
    <div className="-mb-24 flex flex-col gap-8">
      <PayablePanelLockFeedback
        isLockedPayable={isLockedPayable}
        wasUnlockablePayable={wasUnlockablePayable}
      />

      {exportError}

      {/* Mileage specific header */}
      {payableType === 'mileage_allowance' && (
        <ExpenditurePanelHeader
          {...expenditureProps}
          type={payableType}
          mileage={{
            manualInput: payable.mileageDetails?.isManualInput,
            distance: payable.mileageDetails?.distance ?? '',
          }}
        />
      )}

      {/* Per diem specific header */}
      {payableType === 'per_diem_allowance' && (
        <ExpenditurePanelHeader
          {...expenditureProps}
          type={payableType}
          perDiem={{ tripDays: payable.perDiem?.duration ?? 0 }}
        />
      )}

      {/* Other types header */}
      {payableType !== 'mileage_allowance' &&
        payableType !== 'per_diem_allowance' && (
          <ExpenditurePanelHeader
            {...expenditureProps}
            type={payableType}
            supplier={{
              id: supplierId,
              name: supplierName,
              syncStatus: supplierId ? (
                <SupplierSyncStatus
                  supplierId={supplierId}
                  payableSubType={reshapeSubType(payable.subtype)}
                />
              ) : undefined,
              withDetails:
                shouldDisplayDPRInformations && payable.type === 'claimedBill',
              isEditable: !isLockedPayable && isSupplierEditable,
              isDetailsEditable: !isLockedPayable && isSupplierDetailsEditable,
              needsSpanishDprCompliance: shouldDisplayDPRInformations,
            }}
            onSupplierChange={
              !isLockedPayable && isSupplierDetailsEditable
                ? onSupplierChange
                : undefined
            }
            supplierFeedback={(isEditing) =>
              isEditing && (
                <EditAfterExportEditCallout
                  payable={payable}
                  className="mb-8"
                />
              )
            }
          />
        )}

      {payableType === 'invoice' && <InvoiceCardSection payable={payable} />}

      <ReversalsAllocatedSection payable={payable} />

      <CreditNotesCallout
        subtype={payable.subtype}
        creditNoteAmount={payable.functionalAmount}
        allocations={payable.allocations}
      />

      <PayablePanelStatus payable={payable} panelActions={panelActions} />
    </div>
  );
};

/**
 * Utils
 */

// reversal of src/core/modules/bookkeep/payables/containers/PayablePanelContainer/hooks/usePayableQuery/index.ts:105
const reshapeSubType = (
  subType?: Payable['subtype'],
): PayableSubType | undefined => {
  switch (subType) {
    case 'creditNote':
      return 'creditNote';
    case 'subscriptionCard':
      return 'subscription';
    case 'singleUseCard':
      return 'singlePurchase';
    case 'multiUseCard':
      return 'multiUseCard';
    case 'plasticCard':
      return 'physical';
    default:
      return undefined;
  }
};

const reshapePayableType = (rawType: PayableType): RawPayableType => {
  switch (rawType) {
    case 'cardPurchase':
      return 'card_purchase';
    case 'invoicePurchase':
      return 'invoice_purchase';
    case 'claimedBill':
      return 'expense_claim';
    case 'mileageAllowance':
      return 'mileage_allowance';
    case 'perDiemAllowance':
      return 'per_diem_allowance';
    case 'reversal':
      return 'reversal';
    default:
      throw new Error(`Unknown payable type ${rawType}`);
  }
};

const SupplierSyncStatus = ({
  supplierId,
  payableSubType,
}: {
  supplierId: string;
  payableSubType?: PayableSubType;
}) => {
  const { t } = useTranslation('global');
  const { integration } = useAccountingSoftwareName();
  const syncStatus = useGetSupplierSyncWithDefault(supplierId, payableSubType);

  return (
    syncStatus && (
      <SupplierOrMemberSyncStatus
        label={
          syncStatus.synced
            ? getCounterpartySuppliersTitle(t, integration).synced
            : getCounterpartySuppliersTitle(t, integration).notSynced
        }
        tooltipText={getCounterpartySuppliersTooltipText(t, integration)}
        {...syncStatus}
      />
    )
  );
};

const EmployeeSyncStatus = ({
  userId,
  userName,
}: {
  userId: string;
  userName: string;
}) => {
  const { t } = useTranslation('global');
  const syncStatus = useGetMemberSyncStatus(userId);

  return (
    syncStatus && (
      <SupplierOrMemberSyncStatus
        {...syncStatus}
        anchor="simple"
        label={
          syncStatus.synced
            ? getCounterpartyMembersTitle(t, syncStatus.integrationName).synced
            : getCounterpartyMembersTitle(t, syncStatus.integrationName)
                .notSynced
        }
        tooltipText={getCounterpartyMembersCalloutText(
          t,
          syncStatus.integrationName,
        )}
        name={userName}
      />
    )
  );
};

const useOnSupplierChange = ({
  payable,
  isSupplierEditable,
}: {
  payable: Payable;
  isSupplierEditable: boolean;
}) => {
  const { dangerNotif } = useNotifications();

  const [updatePayable] = useUpdatePayable(payable.id);

  const updatePayableQueryCache = useUpdatePayableQueryCache();

  const getUpdatePayableErrorMessage = useUpdatePayableErrorMessage();

  return async (supplier: { id?: string; name: string }) => {
    try {
      if (isSupplierEditable && supplier.id) {
        const { newPayableVersion } = await updatePayable({
          payableVersion: payable.version,
          update: {
            counterparty: {
              id: supplier.id,
              name: supplier.name,
              type: 'supplier',
            },
          },
        });

        track(AnalyticEventName.BOOKKEEP_PAYABLE_ALL_PAYABLE_SAVED, {
          payableId: payable.id,
          editedFields: ['supplier'],
          state: payable.state,
        });

        updatePayableQueryCache(payable.id, {
          version: newPayableVersion,
          counterparty: {
            id: supplier.id,
            name: supplier.name,
            __typename: 'Supplier',
          },
        });
      }
    } catch (error) {
      const errorMessage = getUpdatePayableErrorMessage(error);
      dangerNotif(errorMessage);
      logger.error(errorMessage, {
        team: 'finance-accountant',
        scope: 'payables::panel::supplier::update',
        error,
      });
    }
  };
};
